// src/components/ShopInventory.jsx
// Shows exact bottle/can count at shop by oil type and SKU
// Calculated from: transfers received - sales made at shop

import { OILS, SKUS, SKU_LITRES } from "../constants";
import { fmt, fmtL, OilDot, AlertBar, S } from "../utils/ui";

export function ShopInventory({ state }) {
  const { transfers, sales, prices } = state;

  // Build SKU-wise inventory for shop
  // Start with transfers (incoming to shop)
  const shopStock = {};
  OILS.forEach(o => {
    shopStock[o] = {};
    SKUS.forEach(s => { shopStock[o][s] = 0; });
  });

  // Add transferred units per SKU
  for (const t of transfers) {
    if (shopStock[t.oil] && t.sku) {
      shopStock[t.oil][t.sku] = (shopStock[t.oil][t.sku] || 0) + Number(t.count);
    }
  }

  // Subtract shop sales per SKU
  for (const s of sales) {
    if (s.location === "Shop" && shopStock[s.oil] && s.sku) {
      shopStock[s.oil][s.sku] = (shopStock[s.oil][s.sku] || 0) - Number(s.qty);
    }
  }

  // Calculate totals
  const totalUnits = OILS.reduce((a, o) =>
    a + SKUS.reduce((b, s) => b + Math.max(0, shopStock[o][s]), 0), 0);
  const totalLitres = OILS.reduce((a, o) =>
    a + SKUS.reduce((b, s) => b + Math.max(0, shopStock[o][s]) * SKU_LITRES[s], 0), 0);
  const totalValue = OILS.reduce((a, o) =>
    a + SKUS.reduce((b, s) => b + Math.max(0, shopStock[o][s]) * (prices.oil?.[o]?.[s] || 0), 0), 0);

  const hasAnyStock = OILS.some(o => SKUS.some(s => shopStock[o][s] > 0));

  return (
    <div style={{ padding: "12px 14px" }}>
      <AlertBar
        text="Shows exact bottle and can count at shop. Updates automatically when transfers and sales are logged."
        type="info"
      />

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
        <div style={{ background: "#F7F4EE", borderRadius: 10, padding: "10px 12px", borderLeft: "3px solid #27500A" }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>Total units</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{totalUnits}</div>
        </div>
        <div style={{ background: "#F7F4EE", borderRadius: 10, padding: "10px 12px", borderLeft: "3px solid #185FA5" }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>Total volume</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtL(totalLitres)}</div>
        </div>
        <div style={{ background: "#F7F4EE", borderRadius: 10, padding: "10px 12px", borderLeft: "3px solid #854F0B" }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>Total value</div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(totalValue)}</div>
        </div>
      </div>

      {!hasAnyStock && (
        <div style={{ color: "#aaa", fontSize: 13, textAlign: "center", padding: "24px 0" }}>
          No stock at shop yet. Log a transfer from plant to see inventory here.
        </div>
      )}

      {/* Per oil breakdown */}
      {OILS.map(o => {
        const oilHasStock = SKUS.some(s => shopStock[o][s] > 0);
        if (!oilHasStock) return null;

        const oilTotalUnits  = SKUS.reduce((a, s) => a + Math.max(0, shopStock[o][s]), 0);
        const oilTotalLitres = SKUS.reduce((a, s) => a + Math.max(0, shopStock[o][s]) * SKU_LITRES[s], 0);
        const oilTotalValue  = SKUS.reduce((a, s) => a + Math.max(0, shopStock[o][s]) * (prices.oil?.[o]?.[s] || 0), 0);

        return (
          <div key={o} style={S.card}>
            {/* Oil header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <OilDot oil={o} size={10} />
                <strong style={{ fontSize: 14 }}>{o}</strong>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#27500A" }}>{fmt(oilTotalValue)}</div>
                <div style={{ fontSize: 11, color: "#888" }}>{oilTotalUnits} units · {fmtL(oilTotalLitres)}</div>
              </div>
            </div>

            {/* SKU breakdown */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
              {SKUS.map(s => {
                const units = shopStock[o][s];
                if (units <= 0) return null;
                const isLow = units <= 3;
                return (
                  <div key={s} style={{
                    background: isLow ? "#FAECE7" : "#F7F4EE",
                    borderRadius: 8, padding: "8px 10px",
                    border: isLow ? "1px solid #F5C4B3" : "1px solid transparent",
                  }}>
                    <div style={{ fontSize: 11, color: isLow ? "#993C1D" : "#888", marginBottom: 2 }}>
                      {s} {isLow && "⚠"}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: isLow ? "#993C1D" : "#1a1a1a" }}>
                      {units}
                    </div>
                    <div style={{ fontSize: 10, color: "#888" }}>
                      {fmt(units * (prices.oil?.[o]?.[s] || 0))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Negative stock warning */}
            {SKUS.some(s => shopStock[o][s] < 0) && (
              <div style={{ marginTop: 8, padding: "6px 10px", background: "#FCEBEB", borderRadius: 6, fontSize: 12, color: "#A32D2D" }}>
                ⚠ Some SKUs show negative — check transfer and sales entries for errors.
              </div>
            )}
          </div>
        );
      })}

      {/* Low stock summary */}
      {OILS.some(o => SKUS.some(s => shopStock[o][s] > 0 && shopStock[o][s] <= 3)) && (
        <div style={{ ...S.card, background: "#FFF3CD", border: "1px solid #FAC775" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#633806", marginBottom: 8 }}>
            ⚠ LOW STOCK AT SHOP (3 units or less)
          </div>
          {OILS.map(o =>
            SKUS.map(s => {
              const units = shopStock[o][s];
              if (units <= 0 || units > 3) return null;
              return (
                <div key={`${o}-${s}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                  <span><OilDot oil={o} />{o} {s}</span>
                  <strong style={{ color: "#993C1D" }}>{units} unit{units !== 1 ? "s" : ""} left</strong>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
